import React, { useState, useEffect } from 'react';
import StatusBadge from './common/StatusBadge';
import EmptyState from './common/EmptyState';
import { ClipboardCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TONE = {
  REQUIRED: 'info',
  CONDITIONAL: 'indigo',
  VERIFICATION_REQUIRED: 'warning',
  NOT_REQUIRED: 'neutral',
  NOT_APPLICABLE: 'neutral',
};
const LABEL = {
  REQUIRED: 'Required',
  CONDITIONAL: 'Conditional',
  VERIFICATION_REQUIRED: 'Verify',
  NOT_REQUIRED: 'Not required',
  NOT_APPLICABLE: 'N/A',
};

// Role/equipment-conditional document requirements for an applicant.
export const ConditionalRequirementsPanel = ({ applicationId, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/onboarding/application/${applicationId}/requirements`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error();
        const json = await res.json();
        if (alive) setData(json);
      } catch { if (alive) setError(true); } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [applicationId, token]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 mb-6" data-testid="conditional-requirements">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <ClipboardCheck className="w-5 h-5 text-blue-700" />
        <div>
          <h3 className="font-semibold text-gray-900">Conditional Document Requirements</h3>
          <p className="text-xs text-gray-500">Adapts to the applicant's role &amp; equipment. Only "Required" items should be requested now; regulation-dependent items need verification.</p>
        </div>
        {data && <span className="ml-auto text-xs text-gray-400">{data.application_type}</span>}
      </div>
      {loading ? (
        <EmptyState variant="no-records" compact title="Loading requirements…" message="Evaluating role/equipment-based checklist." testid="req-loading" />
      ) : error ? (
        <EmptyState variant="loading-failure" compact testid="req-error" />
      ) : (
        <ul className="divide-y divide-gray-100" data-testid="req-list">
          {data.requirements.map((r) => (
            <li key={r.key} className="px-4 py-2.5 flex items-start justify-between gap-3" data-testid={`req-${r.key}`}>
              <div className="min-w-0">
                <span className="text-sm font-medium text-gray-800">{r.label}</span>
                <p className="text-xs text-gray-500 break-words">{r.reason}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.received && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5" data-testid={`req-received-${r.key}`}>
                    Received
                  </span>
                )}
                <StatusBadge tone={TONE[r.status] || 'neutral'} testid={`req-status-${r.key}`}>{LABEL[r.status] || r.status}</StatusBadge>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ConditionalRequirementsPanel;
